import { createContext } from 'react';

import type { RealtimeClient } from '@/shared/realtime';

export interface BoardPresence {
  /** Distinct users with the board open (several tabs of one user count once). */
  online: readonly string[];
  /** card id -> users currently editing it. */
  editing: Readonly<Record<string, readonly string[]>>;
}

export const EMPTY_PRESENCE: BoardPresence = { online: [], editing: {} };

export class PresenceStore {
  private readonly boards = new Map<string, BoardPresence>();
  private readonly listeners = new Set<() => void>();

  get = (boardId: string): BoardPresence => this.boards.get(boardId) ?? EMPTY_PRESENCE;

  set(boardId: string, users: readonly string[], editing: readonly (readonly string[])[]): void {
    const byCard: Record<string, string[]> = {};
    for (const [cardId, userId] of editing) {
      if (cardId === undefined || userId === undefined) continue;
      (byCard[cardId] ??= []).push(userId);
    }
    this.boards.set(boardId, { online: [...users], editing: byCard });
    for (const listener of this.listeners) listener();
  }

  clear(boardId: string): void {
    if (this.boards.delete(boardId)) for (const listener of this.listeners) listener();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

export interface RealtimeApi {
  client: RealtimeClient | null;
  presence: PresenceStore;
}

export const RealtimeContext = createContext<RealtimeApi | null>(null);
